import { parseAppMode } from "./app-mode";
import { parseMemberContext } from "./member-context";
import { parseRoles } from "./roles";
import { expectNullable, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
import { parseTenantContext } from "./tenant-context";
export const SESSION_ACCESS_STATES = ["active", "preMember", "disabled", "noVisibleStudents"];
export const FAMILY_ACCESS_STATES = ["available", "notAllowed", "noVisibleStudents"];
export function parseSessionAccessState(value, path = "session.accessState") {
    if (value === "active" || value === "preMember" || value === "disabled" || value === "noVisibleStudents") {
        return value;
    }
    throw new Error(`${path} must be one of: ${SESSION_ACCESS_STATES.join(", ")}`);
}
export function parseFamilyAccessState(value, path = "session.familyAccessState") {
    if (value === "available" || value === "notAllowed" || value === "noVisibleStudents") {
        return value;
    }
    throw new Error(`${path} must be one of: ${FAMILY_ACCESS_STATES.join(", ")}`);
}
function inferAccessState(member) {
    if (!member) {
        return "preMember";
    }
    return "active";
}
function inferFamilyAccessState(member) {
    if (!member) {
        return "notAllowed";
    }
    return member.roles.includes("parent") || member.roles.includes("student") ? "available" : "notAllowed";
}
export function parseSessionResponse(value, path = "session") {
    const record = expectRecord(value, path);
    const member = expectNullable(record.member, parseMemberContext, `${path}.member`);
    return {
        platformUserId: expectString(record.platformUserId, `${path}.platformUserId`),
        tenant: parseTenantContext(record.tenant, `${path}.tenant`),
        roles: parseRoles(record.roles, `${path}.roles`),
        member,
        // Stored sessions created before viewerProfile was introduced remain usable until
        // the normal session refresh replaces this conservative placeholder.
        viewerProfile: record.viewerProfile === undefined
            ? { accountDisplayName: "利用者", selfStudents: [] }
            : parseSessionViewerProfile(record.viewerProfile, `${path}.viewerProfile`),
        accessState: record.accessState === undefined
            ? inferAccessState(member)
            : parseSessionAccessState(record.accessState, `${path}.accessState`),
        accessMessage: expectOptionalString(record.accessMessage, `${path}.accessMessage`),
        familyAccessState: record.familyAccessState === undefined
            ? inferFamilyAccessState(member)
            : parseFamilyAccessState(record.familyAccessState, `${path}.familyAccessState`),
        familyAccessMessage: expectOptionalString(record.familyAccessMessage, `${path}.familyAccessMessage`),
        visibleFamilyStudentCount: record.visibleFamilyStudentCount === undefined
            ? 0
            : expectNumber(record.visibleFamilyStudentCount, `${path}.visibleFamilyStudentCount`),
        appMode: expectNullable(record.appMode, parseAppMode, `${path}.appMode`),
        entryPath: expectString(record.entryPath, `${path}.entryPath`)
    };
}
export function parseSessionViewerProfile(value, path = "session.viewerProfile") {
    const record = expectRecord(value, path);
    return {
        accountDisplayName: expectString(record.accountDisplayName, `${path}.accountDisplayName`),
        selfStudents: Array.isArray(record.selfStudents)
            ? record.selfStudents.map((entry, index) => parseSessionViewerSelfStudent(entry, `${path}.selfStudents[${index}]`))
            : []
    };
}
export function parseSessionViewerSelfStudent(value, path = "session.viewerSelfStudent") {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        displayName: expectString(record.displayName, `${path}.displayName`)
    };
}
export function parseLineLoginRequest(value, path = "lineLoginRequest") {
    const record = expectRecord(value, path);
    return {
        tenantSlug: expectString(record.tenantSlug, `${path}.tenantSlug`),
        idToken: expectString(record.idToken, `${path}.idToken`)
    };
}
export function parseUpdateMemberModeRequest(value, path = "updateMemberModeRequest") {
    const record = expectRecord(value, path);
    return {
        mode: parseAppMode(record.mode, `${path}.mode`)
    };
}
export function parseOwnerOnboardingConsumeRequest(value, path = "ownerOnboardingConsumeRequest") {
    const record = expectRecord(value, path);
    return {
        token: expectString(record.token, `${path}.token`)
    };
}
export function parseMemberInviteConsumeRequest(value, path = "memberInviteConsumeRequest") {
    const record = expectRecord(value, path);
    return {
        token: expectString(record.token, `${path}.token`)
    };
}
export function parseStudentSelfInviteConsumeRequest(value, path = "studentSelfInviteConsumeRequest") {
    const record = expectRecord(value, path);
    return {
        token: expectString(record.token, `${path}.token`)
    };
}
